import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { ArrowLeft, User, Calendar, ClipboardCheck, MessageSquare, Lock } from 'lucide-react';
import { Badge } from '../ui/badge';
import { canViewFullText, REDACTED_TEXT, REDACTED_EXPLANATION } from '../../lib/permissions';

interface AnsweredQuestionMeta {
  title: string;
  sectionTitle: string;
  type: string;
  scaleType?: string;
  followUpQuestion?: string;
}

interface PulseResponse {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  templateId: string;
  templateName: string;
  responses: { [questionId: string]: any };
  answeredQuestions?: { [questionId: string]: AnsweredQuestionMeta };
  selectedSections?: Array<{ id: string; title: string }>;
  reviewCycle?: string;
  status: 'completed' | 'in_progress';
  submittedAt: any;
  createdAt: any;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  questions: Question[];
  order: number;
}

interface Question {
  id: string;
  title: string;
  description?: string;
  type: string;
  options?: string[];
  scaleType?: 'fill' | 'likert' | 'frequency';
  followUpQuestion?: string;
  order: number;
}

interface Template {
  id: string;
  name: string;
  sections: Section[];
}

const PulseResponseDetails: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [response, setResponse] = useState<PulseResponse | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [responseUserManagers, setResponseUserManagers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Whether viewer can see full text (computed after response is loaded)
  const showFullText = currentUser && response ? canViewFullText(
    currentUser.uid,
    currentUser.email,
    userProfile,
    response.userId,
    responseUserManagers
  ) : false;

  useEffect(() => {
    if (submissionId) {
      fetchResponse();
    }
  }, [submissionId]);

  const fetchResponse = async () => {
    try {
      setLoading(true);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Fetch the submission
      const submissionRef = doc(db, 'engagementPulseSubmissions', submissionId!);
      const submissionDoc = await getDoc(submissionRef);
      
      if (!submissionDoc.exists()) {
        setError('Response not found');
        setLoading(false);
        return;
      }
      
      const submissionData = submissionDoc.data();
      const pulseResponse: PulseResponse = {
        id: submissionDoc.id,
        ...submissionData,
        submittedAt: submissionData.submittedAt?.toDate(),
        createdAt: submissionData.createdAt?.toDate()
      } as PulseResponse;
      
      setResponse(pulseResponse);
      
      // Fetch the response user's data to get their managers (for permission check)
      if (pulseResponse.userId) {
        const userRef = doc(db, 'users', pulseResponse.userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setResponseUserManagers(userDoc.data().managers || []);
        }
      }
      
      // Fetch the template to get question details
      if (pulseResponse.templateId) {
        const templateRef = doc(db, 'engagementPulseTemplates', pulseResponse.templateId);
        const templateDoc = await getDoc(templateRef);
        
        if (templateDoc.exists()) {
          setTemplate({
            id: templateDoc.id,
            ...templateDoc.data()
          } as Template);
        }
      }
      
    } catch (err: any) {
      console.error('Error fetching pulse response:', err);
      setError('Failed to load response');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionById = (questionId: string): Question | undefined => {
    if (!template) return undefined;
    
    for (const section of template.sections) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) return question;
    }
    return undefined;
  };

  const getSectionById = (sectionId: string): Section | undefined => {
    if (!template) return undefined;
    return template.sections.find(s => s.id === sectionId);
  };

  const renderScaleTypeLabel = (scaleType: string | undefined, value: any) => {
    switch (scaleType) {
      case 'fill':
        return `${value}%`;
      case 'likert':
        const likertLabels = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
        return likertLabels[Number(value) - 1] || value;
      case 'frequency':
        const frequencyLabels = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];
        return frequencyLabels[Number(value) - 1] || value;
      default:
        return value;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading response...</p>
        </div>
      </div>
    );
  }

  if (error || !response) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <div className="text-destructive mb-4">
              <ClipboardCheck className="h-16 w-16 mx-auto mb-4 opacity-50" />
            </div>
            <h3 className="text-lg font-medium mb-2">Unable to Load Response</h3>
            <p className="text-muted-foreground mb-4">{error || 'Response not found'}</p>
            <Button onClick={() => navigate('/admin/reviews')} variant="outline">
              Return to Reviews
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate(-1)} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">Engagement Pulse Response</h1>
                <p className="text-muted-foreground">
                  Viewing response from {response.userName}
                </p>
              </div>
            </div>
            <Badge variant={
              response.status === 'completed' 
                ? 'default' 
                : 'secondary'
            } className={
              response.status === 'completed'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-yellow-500 hover:bg-yellow-600 text-white'
            }>
              {response.status}
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* User Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>{response.userName}</CardTitle>
                <CardDescription>{response.userEmail}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Submitted:</span>
                <span className="font-medium">
                  {response.submittedAt 
                    ? response.submittedAt.toLocaleDateString() + ' at ' + response.submittedAt.toLocaleTimeString()
                    : 'Not yet submitted'}
                </span>
              </div>
              {response.reviewCycle && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Review Cycle:</span>
                  <span className="font-medium">{response.reviewCycle}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Template:</span>
                <span className="font-medium">{response.templateName || template?.name || 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Questions Answered:</span>
                <span className="font-medium">
                  {Object.keys(response.responses).filter(k => !k.endsWith('_comments') && !k.endsWith('_followup')).length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Redaction Notice */}
        {!showFullText && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3 mb-6">
            <Lock className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Content Redacted</p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                {REDACTED_EXPLANATION}
              </p>
            </div>
          </div>
        )}

        {/* Responses */}
        <Card>
          <CardHeader>
            <CardTitle>Survey Responses</CardTitle>
            <CardDescription>
              All answers submitted for the engagement pulse check
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.entries(response.responses)
                .filter(([questionId]) => !questionId.endsWith('_comments') && !questionId.endsWith('_followup'))
                .map(([questionId, answer], index) => {
                  // First try to get question info from saved metadata (new format)
                  const savedMeta = response.answeredQuestions?.[questionId];
                  // Fall back to looking up in template (old format)
                  const templateQuestion = getQuestionById(questionId);
                  
                  const questionTitle = savedMeta?.title || templateQuestion?.title || `Question ${index + 1}`;
                  const sectionTitle = savedMeta?.sectionTitle || (() => {
                    if (template) {
                      for (const section of template.sections) {
                        if (section.questions.some(q => q.id === questionId)) {
                          return section.title;
                        }
                      }
                    }
                    return '';
                  })();
                  const questionType = savedMeta?.type || templateQuestion?.type;
                  const scaleType = savedMeta?.scaleType || templateQuestion?.scaleType;
                  const followUpQuestionText = savedMeta?.followUpQuestion || templateQuestion?.followUpQuestion;
                  
                  const comments = response.responses[`${questionId}_comments`];
                  const followUp = response.responses[`${questionId}_followup`];
                  
                  return (
                    <div key={questionId} className="border rounded-lg p-4">
                      {sectionTitle && (
                        <Badge variant="outline" className="mb-2">
                          {sectionTitle}
                        </Badge>
                      )}
                      
                      <h4 className="font-medium mb-2">
                        {questionTitle}
                      </h4>
                      
                      {templateQuestion?.description && (
                        <div 
                          className="text-sm text-muted-foreground mb-3 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: templateQuestion.description }}
                        />
                      )}
                      
                      <div className={`bg-muted/50 rounded-lg p-4 ${!showFullText ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-muted-foreground">Response:</span>
                          {questionType === 'rating' && scaleType && (
                            <Badge variant="outline" className="text-xs">
                              {scaleType === 'fill' && 'Fill Scale'}
                              {scaleType === 'likert' && 'Likert Scale'}
                              {scaleType === 'frequency' && 'Frequency Scale'}
                            </Badge>
                          )}
                        </div>
                        <div className="text-lg font-medium">
                          {!showFullText ? REDACTED_TEXT : (
                            questionType === 'rating' && scaleType
                              ? renderScaleTypeLabel(scaleType, answer)
                              : Array.isArray(answer) 
                                ? answer.join(', ') 
                                : String(answer)
                          )}
                        </div>
                      </div>
                      
                      {/* Follow-up question response */}
                      {followUpQuestionText && followUp && (
                        <div className="mt-4 border-t pt-4">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            {followUpQuestionText}
                          </div>
                          <div className={`bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 text-sm ${!showFullText ? 'opacity-60' : ''}`}>
                            {showFullText ? followUp : REDACTED_TEXT}
                          </div>
                        </div>
                      )}
                      
                      {/* Additional comments */}
                      {comments && (
                        <div className="mt-4 border-t pt-4">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Additional Comments:
                          </div>
                          <div className={`bg-muted/30 rounded-lg p-3 text-sm ${!showFullText ? 'opacity-60' : ''}`}>
                            {showFullText ? comments : REDACTED_TEXT}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            
            {Object.keys(response.responses).filter(k => !k.endsWith('_comments') && !k.endsWith('_followup')).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No responses recorded yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PulseResponseDetails;

